<!--
  @component AuthorizationList
  @description Bandeja de casos de autorización. Diseño alineado al sistema
  de diseño del proyecto: Plus Jakarta Sans, paleta navy/teal, cards blancas
  sobre fondo gris, badges semánticos compactos.
-->
<template>
  <div class="auth-list">
    <div class="auth-list__toolbar">
      <el-input
        v-model="searchInput"
        placeholder="Buscar por número, afiliado o prestación..."
        prefix-icon="el-icon-search"
        clearable
        size="small"
        style="width: 300px"
        @input="onSearch"
      />
      <el-select
        v-model="filterStatus"
        placeholder="Todos los estados"
        clearable
        size="small"
        style="width: 170px"
        @change="loadCases"
      >
        <el-option label="Pendiente" value="pending" />
        <el-option label="Aprobada" value="approved" />
        <el-option label="Requiere auditoría" value="requires_audit" />
        <el-option label="Rechazada" value="rejected" />
      </el-select>
      <el-select
        v-model="filterOrigin"
        placeholder="Todos los orígenes"
        clearable
        size="small"
        style="width: 160px"
        @change="loadCases"
      >
        <el-option label="Afiliado" value="member" />
        <el-option label="Prestador" value="provider" />
      </el-select>
      <div style="margin-left: auto">
        <router-link :to="{ name: 'authorization-new' }">
          <el-button type="primary" size="small" icon="el-icon-plus"
            >Nueva solicitud</el-button
          >
        </router-link>
      </div>
    </div>

    <div class="auth-list__card">
      <el-table
        v-loading="isLoading"
        :data="cases"
        size="small"
        border
        :row-style="{ cursor: 'pointer' }"
        style="width: 100%"
        @row-click="goToDetail"
      >
        <el-table-column label="N° Caso" width="80">
          <template slot-scope="{ row }">
            <span class="auth-list__case-num">#{{ row.caseNumber }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Afiliado" min-width="180">
          <template slot-scope="{ row }">
            <el-tooltip
              :content="`${row.memberSnapshot.lastName}, ${
                row.memberSnapshot.firstName
              } · DNI ${row.memberSnapshot.dni || '-'} · Afil. ${
                row.memberSnapshot.memberId || '-'
              } · ${row.memberSnapshot.planName || ''}`"
              placement="top"
              :open-delay="300"
            >
              <div>
                <span class="auth-list__member-name"
                  >{{ row.memberSnapshot.lastName }},
                  {{ row.memberSnapshot.firstName }}</span
                >
                <span class="auth-list__member-sep"> · </span>
                <span class="auth-list__member-plan">{{
                  row.memberSnapshot.planName || "Sin plan"
                }}</span>
              </div>
            </el-tooltip>
          </template>
        </el-table-column>

        <el-table-column
          label="Prestación"
          min-width="220"
          show-overflow-tooltip
        >
          <template slot-scope="{ row }">
            <span class="auth-list__practice-code">{{
              row.practiceSnapshot.code
            }}</span>
            <span class="auth-list__practice-name">{{
              row.practiceSnapshot.name
            }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Categoría" width="130">
          <template slot-scope="{ row }">
            <span class="auth-list__badge auth-list__badge--category">
              {{ categoryLabel(row.practiceSnapshot.category) }}
            </span>
          </template>
        </el-table-column>

        <el-table-column label="Origen" width="100">
          <template slot-scope="{ row }">
            <span class="auth-list__badge auth-list__badge--origin">
              {{ row.origin === "provider" ? "Prestador" : "Afiliado" }}
            </span>
          </template>
        </el-table-column>

        <el-table-column label="Estado" width="160">
          <template slot-scope="{ row }">
            <div
              style="
                display: flex;
                flex-direction: column;
                gap: 4px;
                align-items: flex-start;
              "
            >
              <span class="auth-list__badge" :class="statusClass(row.status)">
                {{ statusLabel(row.status) }}
              </span>
              <span
                v-if="row.wasAudited"
                class="auth-list__badge auth-list__badge--audited"
              >
                <i
                  class="el-icon-s-check"
                  style="margin-right: 3px; font-size: 10px"
                ></i
                >Auditado
              </span>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="Fecha solicitud" width="130">
          <template slot-scope="{ row }">
            <span class="auth-list__date">{{
              row.requestedAt | formatDate
            }}</span>
          </template>
        </el-table-column>
      </el-table>

      <div class="auth-list__pagination">
        <el-pagination
          :current-page="currentPage"
          :page-size="limit"
          :total="total"
          layout="total, prev, pager, next"
          small
          @current-change="onPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import debounce from "debounce";
import authorizationService from "@/services/authorization";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";

/** @component AuthorizationList */
export default {
  name: "AuthorizationList",

  filters: {
    /**
     * @description Trunca un string al largo indicado agregando '...'
     * @param {string} value
     * @param {number} length
     * @returns {string}
     */
    truncate(value, length) {
      if (!value) return "";
      return value.length > length ? value.substring(0, length) + "..." : value;
    },
    /**
     * @description Formatea fecha ISO a dd/MM/yyyy HH:mm
     * @param {string} value
     * @returns {string}
     */
    formatDate(value) {
      if (!value) return "-";
      return format(parseISO(value), "dd/MM/yyyy HH:mm", { locale: es });
    },
    /**
     * @description Formatea DNI con puntos (29191555 → 29.191.555)
     * @param {string|number} value
     * @returns {string}
     */
    formatDni(value) {
      if (!value) return "";
      return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },
  },

  data() {
    return {
      cases: [],
      isLoading: false,
      total: 0,
      currentPage: 1,
      limit: 20,
      searchInput: "",
      filterStatus: "",
      filterOrigin: "",
    };
  },

  created() {
    this.onSearch = debounce(this.loadCases, 350);
    this.loadCases();
  },

  methods: {
    /** @description Carga casos con filtros activos */
    async loadCases() {
      this.isLoading = true;
      try {
        const params = {
          offset: (this.currentPage - 1) * this.limit,
          limit: this.limit,
        };
        if (this.searchInput) params.searchInput = this.searchInput;
        if (this.filterStatus) params.status = this.filterStatus;
        if (this.filterOrigin) params.origin = this.filterOrigin;
        const result = await authorizationService.get(params);
        this.cases = result.docs;
        this.total = result.total;
      } catch (e) {
        this.$message.error("Error al cargar los casos");
      } finally {
        this.isLoading = false;
      }
    },

    /** @param {Object} row */
    goToDetail(row) {
      this.$router.push({
        name: "authorization-detail",
        params: { id: row._id },
      });
    },

    /** @param {number} page */
    onPageChange(page) {
      this.currentPage = page;
      this.loadCases();
    },

    /**
     * @description Devuelve etiqueta legible de la categoría de prestación
     * @param {string} category
     * @returns {string}
     */
    categoryLabel(category) {
      const labels = {
        consultation: "Consulta",
        lab: "Laboratorio",
        imaging: "Imágenes",
        surgery: "Cirugía",
        dental: "Odontología",
        mental_health: "Salud mental",
        rehabilitation: "Rehabilitación",
        emergency: "Urgencias",
        hospitalization: "Internación",
        maternity: "Maternidad",
        medication: "Medicación",
        supply: "Insumos",
        prosthesis: "Prótesis",
        other: "Otros",
      };
      return labels[category] || category || "-";
    },

    /**
     * @description Clase CSS del badge según estado
     * @param {string} status
     * @returns {string}
     */
    statusClass(status) {
      const map = {
        pending: "auth-list__badge--pending",
        approved: "auth-list__badge--approved",
        executed: "auth-list__badge--executed",
        requires_audit: "auth-list__badge--audit",
        rejected: "auth-list__badge--rejected",
      };
      return map[status] || "";
    },

    /**
     * @description Etiqueta legible del estado
     * @param {string} status
     * @returns {string}
     */
    statusLabel(status) {
      const map = {
        pending: "Pendiente",
        approved: "Aprobada",
        executed: "Ejecutada",
        requires_audit: "Requiere auditoría",
        rejected: "Rechazada",
      };
      return map[status] || status;
    },
  },
};
</script>

<style scoped>
.auth-list {
  font-family: "Plus Jakarta Sans", sans-serif;
}

.auth-list__toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.auth-list__card {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.auth-list__case-num {
  font-weight: 700;
  color: #002d5e;
  font-size: 13px;
}

.auth-list__member-name {
  font-weight: 600;
  color: #1a1f36;
  font-size: 13px;
}

.auth-list__member-meta {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 1px;
}

.auth-list__member-plan {
  font-size: 11px;
  font-weight: 600;
  color: #05e5ba;
}

.auth-list__member-sep {
  color: #cbd5e1;
  margin: 0 3px;
  font-size: 11px;
}

.auth-list__practice-code {
  font-weight: 700;
  color: #05e5ba;
  font-size: 12px;
  margin-right: 4px;
}

.auth-list__practice-name {
  color: #1a1f36;
  font-size: 13px;
}

.auth-list__date {
  font-size: 12px;
  color: #6b7294;
  white-space: nowrap;
}

.auth-list__badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.auth-list__badge--category {
  background: #eef1f6;
  color: #002d5e;
}

.auth-list__badge--origin {
  background: #eef1f6;
  color: #002d5e;
}

.auth-list__badge--pending {
  background: #faeeda;
  color: #854f0b;
}

.auth-list__badge--approved {
  background: #eaf3de;
  color: #3b6d11;
}

.auth-list__badge--executed {
  background: #e6f1fb;
  color: #0c447c;
}

.auth-list__badge--audit {
  background: #fcebeb;
  color: #a32d2d;
}

.auth-list__badge--rejected {
  background: #fcebeb;
  color: #a32d2d;
}

.auth-list__badge--audited {
  background: #e6f1fb;
  color: #0c447c;
  font-size: 10px;
  padding: 2px 8px;
}

.auth-list__pagination {
  padding: 12px 16px;
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #f0f2f7;
}
</style>
